import React, { useCallback, useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import api from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Textarea } from "../components/ui/textarea";
import { Label } from "../components/ui/label";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "../components/ui/select";
import { ChevronLeft, LifeBuoy, Send, Plus, ShieldCheck, AlertTriangle, Trash2 } from "lucide-react";
import { toast } from "sonner";
import SwipeRow from "../components/SwipeRow";

const CATEGORIES = [
  { key: "order", label: "Order issue" },
  { key: "vendor", label: "Vendor issue" },
  { key: "refund", label: "Refund request" },
  { key: "scam", label: "Report a scam" },
  { key: "inappropriate", label: "Inappropriate content" },
  { key: "general", label: "General question" },
];

function NewTicketForm({ onCreated }) {
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({ subject: "", category: "general", body: "" });
  const [orderId, setOrderId] = useState("");
  const [orders, setOrders] = useState([]);
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    (async () => {
      try {
        const { data } = await api.get("/orders", { params: { role: "buyer" } });
        setOrders(data);
      } catch { /* ignore */ }
    })();
  }, []);

  const submit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    try {
      const order = orders.find((o) => o.order_id === orderId);
      const payload = {
        subject: form.subject.trim(),
        category: form.category,
        body: form.body.trim(),
        related_order_id: orderId || null,
        related_vendor_id: order?.vendor_id || null,
      };
      const { data } = await api.post("/support/tickets", payload);
      toast.success("Ticket sent — we'll get back to you fast.");
      setForm({ subject: "", category: "general", body: "" });
      setOrderId("");
      setOpen(false);
      onCreated?.(data);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Couldn't create ticket");
    } finally {
      setSubmitting(false);
    }
  };

  if (!open) {
    return (
      <Button
        onClick={() => setOpen(true)}
        className="w-full h-12 rounded-full bg-pink-500 hover:bg-pink-400 text-white font-bold glow-pink"
        data-testid="support-new-ticket-btn"
      >
        <Plus className="w-4 h-4 mr-2" /> Contact Support
      </Button>
    );
  }

  return (
    <form
      onSubmit={submit}
      className="rounded-3xl bg-white/[0.04] border border-white/10 p-5 space-y-4"
      data-testid="support-new-ticket-form"
    >
      <div className="space-y-1.5">
        <Label className="text-zinc-300">What's it about?</Label>
        <Select value={form.category} onValueChange={(v) => setForm({ ...form, category: v })}>
          <SelectTrigger className="h-11 bg-white/5 border-white/10 rounded-2xl text-white" data-testid="support-category-select">
            <SelectValue />
          </SelectTrigger>
          <SelectContent className="bg-zinc-900 border-white/10 text-white">
            {CATEGORIES.map((c) => (
              <SelectItem key={c.key} value={c.key}>{c.label}</SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>

      {orders.length > 0 && (
        <div className="space-y-1.5">
          <Label className="text-zinc-300">Related order (optional)</Label>
          <Select value={orderId || "__none__"} onValueChange={(v) => setOrderId(v === "__none__" ? "" : v)}>
            <SelectTrigger className="h-11 bg-white/5 border-white/10 rounded-2xl text-white" data-testid="support-order-select">
              <SelectValue placeholder="No specific order" />
            </SelectTrigger>
            <SelectContent className="bg-zinc-900 border-white/10 text-white">
              <SelectItem value="__none__">No specific order</SelectItem>
              {orders.slice(0, 30).map((o) => (
                <SelectItem key={o.order_id} value={o.order_id}>
                  #{o.order_id.slice(-6)} · @{o.vendor_name} · ${o.total?.toFixed(2)}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
      )}

      <div className="space-y-1.5">
        <Label className="text-zinc-300">Subject</Label>
        <Input
          required
          value={form.subject}
          onChange={(e) => setForm({ ...form, subject: e.target.value })}
          placeholder="A short summary"
          className="h-11 bg-white/5 border-white/10 rounded-2xl text-white"
          data-testid="support-subject"
        />
      </div>

      <div className="space-y-1.5">
        <Label className="text-zinc-300">Tell us what happened</Label>
        <Textarea
          required
          value={form.body}
          onChange={(e) => setForm({ ...form, body: e.target.value })}
          placeholder="Include order #, dates, screenshots descriptions, etc."
          className="bg-white/5 border-white/10 rounded-2xl text-white min-h-[120px]"
          data-testid="support-body"
        />
      </div>

      <div className="flex gap-2 justify-end">
        <Button
          type="button"
          onClick={() => setOpen(false)}
          className="rounded-full h-11 px-4 bg-white/5 hover:bg-white/10 border border-white/10 text-zinc-300"
          data-testid="support-cancel"
        >
          Cancel
        </Button>
        <Button
          type="submit"
          disabled={submitting}
          className="rounded-full h-11 px-5 bg-pink-500 hover:bg-pink-400 text-white font-bold"
          data-testid="support-submit"
        >
          {submitting ? "Sending…" : "Send ticket"}
        </Button>
      </div>
    </form>
  );
}

function TicketThread({ ticketId, onBack }) {
  const { user } = useAuth();
  const [data, setData] = useState(null);
  const [reply, setReply] = useState("");
  const [sending, setSending] = useState(false);

  const load = useCallback(async () => {
    try {
      const { data } = await api.get(`/support/tickets/${ticketId}`);
      setData(data);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Couldn't open ticket");
    }
  }, [ticketId]);

  useEffect(() => { load(); }, [load]);

  const send = async () => {
    if (!reply.trim()) return;
    setSending(true);
    try {
      await api.post(`/support/tickets/${ticketId}/messages`, { text: reply.trim() });
      setReply("");
      load();
    } catch (e) {
      toast.error("Couldn't send");
    } finally {
      setSending(false);
    }
  };

  const isStaff = !!user && ["moderator", "admin", "senior_admin", "ceo", "master_admin"].includes(user.role);
  const deleteMsg = async (m) => {
    if (!window.confirm("Delete this reply?")) return;
    try {
      await api.delete(`/support/messages/${m.message_id}`);
      toast.success("Reply moved to Trash");
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Couldn't delete");
    }
  };

  if (!data) return <p className="text-zinc-500 text-sm">Loading…</p>;
  const t = data.ticket;
  const closed = ["resolved", "closed"].includes(t.status);

  return (
    <div className="space-y-3" data-testid="support-thread">
      <button
        onClick={onBack}
        className="inline-flex items-center text-sm text-zinc-400 hover:text-white"
        data-testid="support-back"
      >
        <ChevronLeft className="w-4 h-4" /> Back
      </button>
      <div className="rounded-2xl bg-white/[0.04] border border-white/10 p-4">
        <div className="flex flex-wrap items-center gap-2">
          <p className="font-bold">{t.subject}</p>
          <span className="text-[10px] uppercase tracking-widest px-2 py-0.5 rounded-full bg-pink-500/15 text-pink-200 border border-pink-400/30">{t.category}</span>
          <span className="text-[10px] uppercase tracking-widest px-2 py-0.5 rounded-full bg-white/5 border border-white/10">{t.status}</span>
          {t.priority === "high" && (
            <span className="text-[10px] uppercase tracking-widest px-2 py-0.5 rounded-full bg-red-500/15 text-red-200 border border-red-400/30 inline-flex items-center gap-1">
              <AlertTriangle className="w-3 h-3" /> Priority
            </span>
          )}
        </div>
      </div>
      <div className="space-y-2">
        {data.messages.map((m) => {
          const isMine = m.sender_id === user?.user_id;
          const isAdmin = m.sender_role === "admin" || m.sender_role === "master_admin";
          const canDelete = isMine || isStaff;
          return (
            <div
              key={m.message_id}
              className={`group relative p-3 rounded-2xl border ${
                isAdmin
                  ? "bg-cyan-400/[0.07] border-cyan-400/30"
                  : isMine
                  ? "bg-pink-500/[0.07] border-pink-400/20"
                  : "bg-white/[0.04] border-white/10"
              }`}
              data-testid={`support-msg-${m.message_id}`}
            >
              <p className="text-[11px] text-zinc-400 mb-1 uppercase tracking-widest font-bold inline-flex items-center gap-1">
                {isAdmin && <ShieldCheck className="w-3 h-3 text-cyan-300" />}
                {m.sender_name}
              </p>
              <p className="text-sm whitespace-pre-wrap">{m.text}</p>
              {canDelete && (
                <button
                  onClick={() => deleteMsg(m)}
                  aria-label="Delete reply"
                  className="absolute top-2 right-2 opacity-0 group-hover:opacity-100 transition-opacity w-7 h-7 grid place-items-center rounded-full text-zinc-500 hover:text-red-400 hover:bg-red-500/10"
                  data-testid={`support-msg-delete-${m.message_id}`}
                >
                  <Trash2 className="w-3.5 h-3.5" />
                </button>
              )}
            </div>
          );
        })}
      </div>
      {!closed && (
        <div className="flex items-end gap-2">
          <Textarea
            value={reply}
            onChange={(e) => setReply(e.target.value)}
            placeholder="Add a reply…"
            className="bg-white/5 border-white/10 rounded-2xl text-white min-h-[60px]"
            data-testid="support-reply-input"
          />
          <Button
            onClick={send}
            disabled={!reply.trim() || sending}
            className="rounded-full bg-pink-500 hover:bg-pink-400 text-white h-11 px-4"
            data-testid="support-reply-send"
          >
            <Send className="w-3.5 h-3.5" />
          </Button>
        </div>
      )}
      {closed && <p className="text-xs text-zinc-500 text-center">This ticket is {t.status}. Open a new ticket if you need more help.</p>}
    </div>
  );
}

export default function Support() {
  const { ticketId } = useParams();
  const navigate = useNavigate();
  const [tickets, setTickets] = useState([]);

  const load = useCallback(async () => {
    try {
      const { data } = await api.get("/support/tickets/me");
      setTickets(data);
    } catch { /* ignore */ }
  }, []);

  useEffect(() => { load(); }, [load]);

  const deleteTicket = async (t) => {
    if (!window.confirm(`Delete "${t.subject}"? You'll find it in your Trash bin for 24 hours.`)) return;
    try {
      await api.delete(`/support/tickets/${t.ticket_id}`);
      setTickets((arr) => arr.filter((x) => x.ticket_id !== t.ticket_id));
      toast.success("Ticket moved to Trash");
    } catch (err) {
      toast.error(err.response?.data?.detail || "Couldn't delete");
    }
  };

  if (ticketId) {
    return (
      <div className="max-w-2xl mx-auto px-4 py-6 pb-24" data-testid="support-page">
        <TicketThread ticketId={ticketId} onBack={() => navigate("/support")} />
      </div>
    );
  }

  return (
    <div className="max-w-2xl mx-auto px-4 py-6 space-y-5 pb-24" data-testid="support-page">
      <div className="flex items-center gap-3">
        <div className="w-12 h-12 rounded-2xl bg-pink-500/15 grid place-items-center border border-pink-400/30">
          <LifeBuoy className="w-6 h-6 text-pink-300" />
        </div>
        <div>
          <h1 className="text-2xl font-black tracking-tighter">Customer Support</h1>
          <p className="text-sm text-zinc-400">Orders, refunds, scams, bad content — we've got you.</p>
        </div>
      </div>

      <NewTicketForm onCreated={() => load()} />

      <h2 className="text-base font-black tracking-tighter pt-2">Your tickets</h2>
      {tickets.length === 0 && (
        <p className="text-zinc-500 text-sm" data-testid="support-empty">No tickets yet.</p>
      )}
      <div className="space-y-2">
        {tickets.map((t) => (
          <SwipeRow
            key={t.ticket_id}
            onDelete={() => deleteTicket(t)}
            testIdPrefix={`support-ticket-${t.ticket_id}`}
          >
            <div className="w-full flex items-center gap-1 bg-white/[0.04] border border-white/10 rounded-2xl hover:bg-white/[0.07]">
              <button
                onClick={() => navigate(`/support/${t.ticket_id}`)}
                className="flex-1 text-left p-3"
                data-testid={`support-ticket-row-${t.ticket_id}`}
              >
                <div className="flex items-center gap-3">
                  <div className="min-w-0 flex-1">
                    <p className="font-bold truncate">{t.subject}</p>
                    <p className="text-xs text-zinc-400 truncate">{t.category} · {new Date(t.updated_at).toLocaleString()}</p>
                  </div>
                  <span className="text-[10px] uppercase tracking-widest px-2 py-0.5 rounded-full bg-white/5 border border-white/10">{t.status}</span>
                </div>
              </button>
              <button
                onClick={() => deleteTicket(t)}
                aria-label="Delete ticket"
                className="w-9 h-9 mr-2 grid place-items-center rounded-full text-zinc-500 hover:text-red-400 hover:bg-red-500/10"
                data-testid={`support-ticket-delete-${t.ticket_id}`}
              >
                <Trash2 className="w-4 h-4" />
              </button>
            </div>
          </SwipeRow>
        ))}
      </div>
    </div>
  );
}
